import { Icon } from '@natera/material/lib/icon'
import { useEffect, useState } from 'react'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { InfoTip, LocalIcon, RadioButton, Text } from '~/shared/ui'
import { NO_DATA_PLACEHOLDER } from '../../config'
import { ConfidenceBadge } from '../patients/confidence-badge'
import {
  CardDivider,
  ContradictionChip,
  FieldRow,
  StatusIcon,
  VendorField,
  formatCriterion,
  getFieldStatus,
  resolveFieldStatus,
} from './pa-card-shared'

export function PaMatchCard({
  record,
  isSystemSelected,
  isSelectable = false,
  isSelected = false,
  onSelect,
  onHide,
  canHide = false,
  showContradictions = false,
  hoveredField = null,
  documentFields,
  connectUrl,
  isUserCreated = false,
  isMatchingV2 = false,
}: {
  record: apiClient.PatientPaRecord
  isSystemSelected: boolean
  isSelectable?: boolean
  isSelected?: boolean
  onSelect?: () => void
  onHide?: () => void
  canHide?: boolean
  showContradictions?: boolean
  hoveredField?: string | null
  documentFields?: Record<string, string | undefined>
  connectUrl?: string
  isUserCreated?: boolean
  isMatchingV2?: boolean
}) {
  const similarity = record.similarity
  const matchedOn = record.matched_on ?? []
  const contradictedOn = record.contradicted_on ?? []
  const [isContradictedExpanded, setIsContradictedExpanded] = useState(showContradictions)

  useEffect(() => {
    setIsContradictedExpanded(showContradictions)
  }, [showContradictions])

  const fieldMap: Record<
    string,
    { record?: string | null; extracted?: string | null; isDate?: boolean }
  > = {
    date_of_birth: {
      record: record.patient_dob,
      extracted: documentFields?.member_dob,
      isDate: true,
    },
    payor_name: { record: record.payor_id, extracted: documentFields?.insurance_company },
    test_name: { record: record.test_name, extracted: documentFields?.test_name },
    date_of_service: {
      record: record.service_date,
      extracted: documentFields?.service_date_from,
      isDate: true,
    },
    member_id: {
      record: record.policy_number?.replace(/^\[|\]$/g, ''),
      extracted: documentFields?.member_id,
    },
    reference_token: {
      record: record.reference_token,
      extracted: documentFields?.reference_number,
    },
    authorization_token: {
      record: record.authorization_token,
      extracted: documentFields?.authorization_code,
    },
    cpt_codes: { record: record.cpt_codes, extracted: documentFields?.cpt_code },
  }

  function status(fieldKey: string) {
    if (!isMatchingV2) return getFieldStatus(fieldKey, matchedOn, contradictedOn)
    const f = fieldMap[fieldKey]
    return resolveFieldStatus(fieldKey, matchedOn, contradictedOn, f?.record, f?.extracted, {
      isDate: f?.isDate,
    })
  }

  const allContradictions = isMatchingV2
    ? Object.keys(fieldMap).filter((key) => status(key) === 'contradiction')
    : contradictedOn

  return (
    <div
      className={cn(
        'bg-surface-lowest flex h-full flex-col overflow-hidden rounded-lg border whitespace-nowrap',
        {
          'border-chip-status-success-content border-2': isSystemSelected,
          'border-primary-main border-2': isSelectable && isSelected,
          'border-divider': !isSystemSelected && !(isSelectable && isSelected),
        },
      )}
    >
      {isSelectable ? (
        <div className="border-divider min-h-[52px] border-b">
          <button
            className="bg-surface-lowest relative flex h-full w-full cursor-pointer items-center justify-between border-none px-4 py-3"
            type="button"
            onClick={onSelect}
          >
            <RadioButton
              label={isMatchingV2 && isUserCreated ? 'Added by user' : 'Match'}
              withinButton
              checked={isSelected}
              onChange={() => onSelect?.()}
            />
          </button>
        </div>
      ) : (
        <div
          className={cn('flex min-h-[52px] items-center justify-between border-b px-4 py-3', {
            'border-chip-status-success-content/30 bg-chip-status-success-content/5':
              isSystemSelected,
            'border-divider': !isSystemSelected,
          })}
        >
          <Text
            className={cn({
              'text-chip-status-success-content': isSystemSelected,
              'text-text-secondary': !isSystemSelected,
            })}
            appearance="CAPTION"
          >
            {isSystemSelected ? 'System selected' : 'PA Case'}
          </Text>
          {similarity != null && (
            <ConfidenceBadge
              confidencePercentage={Math.round(similarity * 100)}
              label={similarity.toFixed(2)}
            />
          )}
        </div>
      )}

      <div className="px-4 pt-2">
        <Text appearance="OVERLINE">Natera Patient Data</Text>
      </div>

      <div className="flex flex-col gap-2 px-4 py-3">
        <Text
          appearance="SUBTITLE"
          placeholder={NO_DATA_PLACEHOLDER}
          truncated
          withTooltip
          copyable
        >
          {record.patient_first_name} {record.patient_last_name}
        </Text>
        <FieldRow
          label="Birth Date"
          value={record.patient_dob ? formatDateDefault(record.patient_dob) : null}
          status={status('date_of_birth')}
          hoveredField={hoveredField}
          hoveredFieldKey="birth_date"
          extractedValue={documentFields?.member_dob}
          isDate
        />
        <FieldRow
          label="Payor Name"
          value={record.payor_id}
          status={status('payor_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="insurance_company"
          extractedValue={documentFields?.insurance_company}
        />
        <FieldRow
          label="Test Name"
          value={record.test_name}
          status={status('test_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="test_name"
          extractedValue={documentFields?.test_name}
        />
        <FieldRow
          label="Date of Service"
          value={record.service_date ? formatDateDefault(record.service_date) : null}
          status={status('date_of_service')}
          hoveredField={hoveredField}
          hoveredFieldKey="date_of_service"
          extractedValue={documentFields?.service_date_from}
          isDate
        />
      </div>

      <CardDivider />

      <div className="flex flex-col gap-3 px-4 py-3">
        <FieldRow
          label="Member ID"
          value={record.policy_number}
          status={status('member_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="member_id"
          extractedValue={documentFields?.member_id}
        />
        <div
          className={cn('flex h-5 items-center gap-1 px-0.5 transition-colors', {
            'bg-secondary-focused':
              hoveredField === 'case_number' &&
              !!record.case_id &&
              !!documentFields?.case_number &&
              record.case_id.trim().toLowerCase() ===
                documentFields.case_number.trim().toLowerCase(),
            'bg-error-focused':
              hoveredField === 'case_number' &&
              !!record.case_id &&
              !!documentFields?.case_number &&
              record.case_id.trim().toLowerCase() !==
                documentFields.case_number.trim().toLowerCase(),
          })}
        >
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2 whitespace-nowrap">
            <div className="flex shrink-0 items-center gap-0.5">
              <Text
                className="text-text-secondary"
                appearance="BODY2"
              >
                LIMS Case ID
              </Text>
              <InfoTip iconSize="16">
                <Text
                  className="text-text-secondary max-w-[265px] pr-6"
                  appearance="CAPTION"
                >
                  AKA: Case ID, LIMS Case ID.
                  <br />A unique identifier assigned by Natera Sales Order System (SOS) to a test
                  that ties back to the physical sample we receive from a patient.
                </Text>
              </InfoTip>
            </div>
            {record.case_id && (
              <Text
                className="min-w-0 text-right"
                appearance="BODY2"
                truncated
                withTooltip
                copyable
              >
                {record.case_id}
              </Text>
            )}
          </div>
          <StatusIcon status={isMatchingV2 ? 'not_applicable' : status('lims_case_id')} />
        </div>
        <FieldRow
          label="Reference Number"
          value={record.reference_token}
          status={status('reference_token')}
          hoveredField={hoveredField}
          hoveredFieldKey="reference_number"
          extractedValue={documentFields?.reference_number}
        />
        <FieldRow
          label="Authorization Code"
          value={record.authorization_token}
          status={status('authorization_token')}
        />
        <FieldRow
          label="CPT Codes"
          value={record.cpt_codes}
          status={status('cpt_codes')}
          hoveredField={hoveredField}
          hoveredFieldKey="cpt_code"
          extractedValue={documentFields?.cpt_code}
        />
      </div>

      <CardDivider />

      <div className="flex flex-col gap-3 px-4 py-3">
        <VendorField
          label="Prior Auth Vendor"
          value={record.vendor_given_to}
        />
      </div>

      <CardDivider />

      <div className="flex flex-col">
        <button
          className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-4 pt-4 text-left"
          type="button"
          onClick={() => setIsContradictedExpanded((v) => !v)}
        >
          <Text
            className="flex-1"
            appearance="OVERLINE"
          >
            Contradicted on{isContradictedExpanded ? '' : ` (${allContradictions.length})`}
          </Text>
          <Icon
            className="text-[20px]"
            children={isContradictedExpanded ? 'expand_less' : 'expand_more'}
          />
        </button>
        {isContradictedExpanded && (
          <div className="flex flex-wrap gap-1 px-4 py-3 whitespace-normal">
            {allContradictions.length > 0 ? (
              allContradictions.map((criterion) => (
                <ContradictionChip key={criterion}>{formatCriterion(criterion)}</ContradictionChip>
              ))
            ) : (
              <Text
                className="text-text-secondary"
                appearance="BODY2"
              >
                None
              </Text>
            )}
          </div>
        )}
      </div>

      <div className="grow" />
      <div className="border-divider flex items-center justify-between border-t p-4">
        {connectUrl ? (
          <a
            className="text-text-link flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 no-underline"
            target="_blank"
            href={connectUrl}
          >
            <Text appearance="BODY2">More</Text>
            <LocalIcon
              name="openInNew"
              size="18"
            />
          </a>
        ) : (
          <span className="text-text-link flex items-center gap-1">
            <Text appearance="BODY2">More</Text>
            <LocalIcon
              name="openInNew"
              size="18"
            />
          </span>
        )}
        <button
          className={cn(
            'flex cursor-pointer items-center gap-1 border-none bg-transparent p-0',
            canHide ? 'text-text-secondary' : 'text-text-disabled cursor-not-allowed',
          )}
          type="button"
          disabled={!canHide}
          onClick={onHide}
        >
          <Text appearance="BODY2">Hide</Text>
          <Icon
            className="text-[18px]"
            children="visibility_off"
          />
        </button>
      </div>
    </div>
  )
}
